import { Logger } from '@egw/logger'
import { Capacitor } from '@capacitor/core'

const log = new Logger('User Gesture Detector')

export enum WebAuthnOperation {
  REGISTRATION = 'REGISTRATION',
  AUTHENTICATION = 'AUTHENTICATION',
}

export enum BrowserType {
  CHROME = 'CHROME',
  FIREFOX = 'FIREFOX',
  SAFARI = 'SAFARI',
  EDGE = 'EDGE',
  UNKNOWN = 'UNKNOWN',
}

interface UserGestureState {
  lastGestureTime: number
  isActive: boolean
  eventSource: Event | null
}

interface UserActivationAPI {
  isActive?: boolean
  hasBeenActive?: boolean
}

export class UserGestureDetector {
  private static instance: UserGestureDetector | null = null
  private gestureState: UserGestureState
  private browserType: BrowserType
  private readonly GESTURE_TIMEOUT_MS = 5000 // 5 seconds
  private readonly SAFARI_GESTURE_TIMEOUT_MS = 1000 // Safari is stricter
  private eventListeners: (() => void)[] = []

  private constructor() {
    this.gestureState = {
      lastGestureTime: 0,
      isActive: false,
      eventSource: null,
    }
    this.browserType = this.detectBrowser()
    this.initializeGestureTracking()
  }

  static getInstance(): UserGestureDetector {
    if (!UserGestureDetector.instance) {
      UserGestureDetector.instance = new UserGestureDetector()
    }
    return UserGestureDetector.instance
  }

  private detectBrowser(): BrowserType {
    if (typeof window === 'undefined') {
      return BrowserType.UNKNOWN
    }

    const userAgent = navigator.userAgent.toLowerCase()
    const isIOS = /iphone|ipad|ipod/.test(userAgent)
    const isSafari = userAgent.includes('safari') && !/chrome|crios|fxios/.test(userAgent)
    const isChrome = userAgent.includes('chrome') && !userAgent.includes('edg')
    const isFirefox = /firefox|fxios/.test(userAgent)
    const isEdge = userAgent.includes('edg')

    if (isIOS && isSafari) {
      return BrowserType.SAFARI
    }
    if (isSafari) {
      return BrowserType.SAFARI
    }
    if (isChrome) {
      return BrowserType.CHROME
    }
    if (isFirefox) {
      return BrowserType.FIREFOX
    }
    if (isEdge) {
      return BrowserType.EDGE
    }

    return BrowserType.UNKNOWN
  }

  private initializeGestureTracking(): void {
    if (typeof window === 'undefined') {
      return
    }

    const gestureEvents = ['click', 'touchstart', 'keydown', 'mousedown']
    const handleGesture = (event: Event): void => {
      this.gestureState = {
        lastGestureTime: Date.now(),
        isActive: true,
        eventSource: event,
      }

      // Reset active state after a short delay
      setTimeout(() => {
        this.gestureState.isActive = false
      }, 100)
    }

    gestureEvents.forEach((eventType) => {
      window.addEventListener(eventType, handleGesture, { passive: true, capture: true })
      this.eventListeners.push(() => {
        window.removeEventListener(eventType, handleGesture, { capture: true })
      })
    })
  }

  /**
   * Check if user gesture is currently active using navigator.userActivation API
   */
  isGestureActive(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false
    }

    // Use native userActivation API if available (modern browsers)
    const userActivation = navigator.userActivation as UserActivationAPI | undefined
    if (userActivation?.isActive !== undefined) {
      const isActive = userActivation.isActive
      log.debug('User activation status from API', { isActive })
      return isActive
    }

    // Fallback to our tracking
    const timeSinceLastGesture = this.getTimeSinceLastGesture()
    const timeout = this.browserType === BrowserType.SAFARI ? this.SAFARI_GESTURE_TIMEOUT_MS : this.GESTURE_TIMEOUT_MS

    const isActive = this.gestureState.isActive || timeSinceLastGesture < timeout
    log.debug('User activation status from tracking', {
      isActive,
      timeSinceLastGesture,
      timeout,
      browserType: this.browserType,
    })

    return isActive
  }

  /**
   * Get time in milliseconds since last user gesture
   */
  getTimeSinceLastGesture(): number {
    if (this.gestureState.lastGestureTime === 0) {
      return Infinity
    }
    return Date.now() - this.gestureState.lastGestureTime
  }

  /**
   * Validate that the event source is a legitimate user event
   */
  validateEventSource(event: Event | null): boolean {
    if (!event) {
      return false
    }

    // Check if event is trusted (user-initiated)
    if (!event.isTrusted) {
      log.warn('Event is not trusted', { eventType: event.type })
      return false
    }

    // Validate event type
    const validEventTypes = ['click', 'touchstart', 'keydown', 'mousedown', 'submit']
    if (!validEventTypes.includes(event.type)) {
      log.warn('Invalid event type for gesture', { eventType: event.type })
      return false
    }

    return true
  }

  /**
   * Check if a WebAuthn operation requires user gesture based on browser and context
   */
  requiresUserGesture(operation: WebAuthnOperation): boolean {
    // Native platforms don't require browser gesture checks
    if (Capacitor.isNativePlatform()) {
      return false
    }

    // All WebAuthn operations require gestures in web context
    // Browser-specific strictness varies but all require it
    switch (this.browserType) {
      case BrowserType.SAFARI:
        // Safari is very strict, especially iOS
        return true
      case BrowserType.CHROME:
      case BrowserType.EDGE:
        // Chrome/Edge are strict but allow some programmatic flows
        return true
      case BrowserType.FIREFOX:
        // Firefox is more lenient but still requires gestures
        return true
      default:
        // Default to requiring gesture for security
        return true
    }
  }

  /**
   * Check if we're in an iframe context (stricter requirements)
   */
  isInIframe(): boolean {
    if (typeof window === 'undefined') {
      return false
    }
    try {
      return window.self !== window.top
    } catch {
      // Cross-origin iframe will throw
      return true
    }
  }

  /**
   * Validate gesture requirements before WebAuthn operation
   */
  validateGestureRequirements(operation: WebAuthnOperation): {
    isValid: boolean
    reason?: string
    requiresUserAction: boolean
  } {
    if (!this.requiresUserGesture(operation)) {
      return { isValid: true, requiresUserAction: false }
    }

    const isActive = this.isGestureActive()
    const timeSinceGesture = this.getTimeSinceLastGesture()
    const inIframe = this.isInIframe()
    const timeout = this.browserType === BrowserType.SAFARI ? this.SAFARI_GESTURE_TIMEOUT_MS : this.GESTURE_TIMEOUT_MS

    // Check if gesture is still valid
    if (!isActive && timeSinceGesture > timeout) {
      const reason = `User gesture expired. Last gesture was ${timeSinceGesture}ms ago (timeout: ${timeout}ms)`
      log.warn('Gesture validation failed', {
        operation,
        reason,
        browserType: this.browserType,
        inIframe,
      })
      return {
        isValid: false,
        reason,
        requiresUserAction: true,
      }
    }

    // Additional checks for iframe context
    if (inIframe && timeSinceGesture > timeout / 2) {
      const reason = 'Iframe context requires more recent user gesture'
      log.warn('Gesture validation failed in iframe', {
        operation,
        reason,
        browserType: this.browserType,
      })
      return {
        isValid: false,
        reason,
        requiresUserAction: true,
      }
    }

    // Safari-specific strict checks
    if (this.browserType === BrowserType.SAFARI && timeSinceGesture > this.SAFARI_GESTURE_TIMEOUT_MS) {
      const reason = 'Safari requires gesture within 1 second'
      log.warn('Safari gesture validation failed', {
        operation,
        reason,
        timeSinceGesture,
      })
      return {
        isValid: false,
        reason,
        requiresUserAction: true,
      }
    }

    log.debug('Gesture validation passed', {
      operation,
      browserType: this.browserType,
      timeSinceGesture,
      isActive,
      inIframe,
    })

    return {
      isValid: true,
      requiresUserAction: false,
    }
  }

  /**
   * Get browser-specific guidance message for user
   */
  getUserGuidanceMessage(): string {
    switch (this.browserType) {
      case BrowserType.SAFARI:
        return 'Please click or tap to continue with authentication'
      case BrowserType.CHROME:
      case BrowserType.EDGE:
        return 'Please interact with the page to continue'
      case BrowserType.FIREFOX:
        return 'User interaction required to continue'
      default:
        return 'Please click or interact with the page to continue'
    }
  }

  /**
   * Cleanup event listeners
   */
  destroy(): void {
    this.eventListeners.forEach((cleanup) => {
      cleanup()
    })
    this.eventListeners = []
  }

  /**
   * Get current browser type
   */
  getBrowserType(): BrowserType {
    return this.browserType
  }
}
